// Spec 48 ai-ux — prompt_suggestion_rail.
//
// "Horizontal rail of suggestion chips above the composer on empty
// threads. Suggestions are scope-aware — never chips the caller cannot
// act on. Clicking populates composer as a draft, not auto-send."
// (spec 48 line 928)
//
// a11y per spec 48:
//   - Rail is role="region" with aria-label="Prompt suggestions"
//   - Each chip is a button; Tab navigates in visual order; Enter
//     populates composer
//   - Chips POPULATE the composer — never auto-send
//   - Horizontal scroll uses arrow keys when focus is inside the rail

import { forwardRef, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export interface PromptSuggestion {
  readonly id: string;
  /** Display text + the prompt populated into the composer. */
  readonly text: string;
}

export interface PromptSuggestionRailProps extends HTMLAttributes<HTMLDivElement> {
  readonly suggestions: readonly PromptSuggestion[];
  /** Click handler — caller populates the composer (NEVER auto-sends per spec rule 962). */
  readonly onSuggestionPick: (suggestion: PromptSuggestion) => void;
  /** Optional refresh handler. When set, renders a "Try others" button. */
  readonly onRefresh?: () => void;
  /** Override rail aria-label. */
  readonly railAriaLabel?: string;
  /** Override "Try others" refresh label. */
  readonly refreshLabel?: string;
  /** Empty-state fallback text when suggestions=[]. */
  readonly emptyText?: string;
}

export const PromptSuggestionRail = forwardRef<HTMLDivElement, PromptSuggestionRailProps>(
  (
    {
      suggestions,
      onSuggestionPick,
      onRefresh,
      railAriaLabel = "Prompt suggestions",
      refreshLabel = "Try others",
      emptyText = "No suggestions right now — ask anything.",
      className,
      ...rest
    },
    ref,
  ) => {
    if (suggestions.length === 0) {
      return (
        <div
          ref={ref}
          role="region"
          aria-label={railAriaLabel}
          data-component="prompt-suggestion-rail"
          data-state="empty"
          className={cx("nai-prompt-suggestion-rail", "nai-prompt-suggestion-rail--empty", className)}
          {...rest}
        >
          <span
            data-component="prompt-suggestion-rail-empty"
            className="nai-prompt-suggestion-rail-empty"
          >
            {emptyText}
          </span>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        role="region"
        aria-label={railAriaLabel}
        data-component="prompt-suggestion-rail"
        className={cx("nai-prompt-suggestion-rail", className)}
        {...rest}
      >
        <div
          data-component="prompt-suggestion-rail-chips"
          className="nai-prompt-suggestion-rail-chips"
        >
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              data-component="prompt-suggestion-chip"
              className="nai-prompt-suggestion-chip"
              onClick={() => onSuggestionPick(s)}
            >
              {s.text}
            </button>
          ))}
        </div>
        {onRefresh !== undefined && (
          <button
            type="button"
            data-component="prompt-suggestion-rail-refresh"
            className="nai-prompt-suggestion-rail-refresh"
            onClick={onRefresh}
          >
            {refreshLabel}
          </button>
        )}
      </div>
    );
  },
);
PromptSuggestionRail.displayName = "PromptSuggestionRail";
